import { EmployeeSocialFactor, EmploymentLevel, Gender, OccupationType } from "../../entities/contractorEmployee";
import { SocialSpendCertificate, SocialSpendEntityType } from "../../entities/socialSpend";
import { SocialCommitmentTypeHour } from "../packageLabourHourCommitment";
import { SocialCommitmentTypeSpend } from "../packageSocialSpendCommitment";
import { LOCAL_JOBS_FIRST_DISTANCE } from "./constants";
import { LabourHourTargetDenominator, ProjectLabourHourCommitmentType } from "../projectLabourHourCommitment/enums";
// Re-export for backward compatibility
export { LOCAL_JOBS_FIRST_DISTANCE };
// Victoria, Australia - Labour Hour Commitment Criteria
// Using factory function to avoid circular dependency at module load time
const createVictoriaAustraliaLabourHourCommitmentCriteria = () => ({
    [SocialCommitmentTypeHour.AboriginalTorresStraitIslanderParticipation]: {
        colour: "#FFAB91",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 4.1
    },
    [SocialCommitmentTypeHour.BepManagementHours]: {
        colour: "#15cf74",
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        occupationType: [OccupationType.ManagementSupervisorSpecialistLabourRoles],
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalHoursManagement,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 35
    },
    [SocialCommitmentTypeHour.BepNonTradeHours]: {
        colour: "#15cf74",
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        occupationType: [OccupationType.NonTradePosition],
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalHoursNonTrade,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 7
    },
    [SocialCommitmentTypeHour.BepTradeHours]: {
        colour: "#15cf74",
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        occupationType: [OccupationType.TradeRole],
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalHoursTrade,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 3
    },
    [SocialCommitmentTypeHour.FemaleApprenticeAndTrainee]: {
        colour: "#28A745",
        employmentLevel: [EmploymentLevel.Apprentice, EmploymentLevel.Trainee],
        gender: [Gender.Female],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.ACTHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 4
    },
    [SocialCommitmentTypeHour.FemaleParticipation]: undefined,
    [SocialCommitmentTypeHour.LocalJobsFirst]: {
        colour: "#90CAF9",
        employmentLevel: [],
        gender: [],
        maxDistance: LOCAL_JOBS_FIRST_DISTANCE,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 90
    },
    [SocialCommitmentTypeHour.MPSGHoursRequirement]: {
        colour: "#3f99fa",
        employmentLevel: [
            EmploymentLevel.Apprentice,
            EmploymentLevel.Trainee,
            EmploymentLevel.Cadet
        ],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 10
    },
    [SocialCommitmentTypeHour.VictorianPriorityWorkforce]: {
        colour: "#1565C0",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [
            EmployeeSocialFactor.AsylumSeekerRefugee,
            EmployeeSocialFactor.DisengagedYouth,
            EmployeeSocialFactor.LongTermUnemployed,
            EmployeeSocialFactor.PersonWithDisability,
            EmployeeSocialFactor.Veteran,
            EmployeeSocialFactor.PersonsAffectedByFamilyViolence,
            EmployeeSocialFactor.ExOffender
        ],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 2
    }
});
export const victoriaAustraliaLabourHourCommitmentCriteria = createVictoriaAustraliaLabourHourCommitmentCriteria();
// Victoria, Australia - Social Spend Commitment Criteria
export const victoriaAustraliaSocialSpendCommitmentCriteria = {
    [SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend]: {
        certifications: [],
        colour: "#A5D6A7",
        entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
        postCodes: [],
        targetValue: 1.5
    },
    [SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend]: {
        certifications: [],
        colour: "#FFE57F",
        entityTypes: [
            SocialSpendEntityType.DisabilityEnterprise,
            SocialSpendEntityType.SocialEnterprise
        ],
        postCodes: [],
        targetValue: 5
    }
};
// South Australia - Labour Hour Commitment Criteria
const createSouthAustraliaLabourHourCommitmentCriteria = () => ({
    // Aboriginal Employment
    [SocialCommitmentTypeHour.AboriginalEmployment]: {
        colour: "#FFAB91",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 4
    },
    // Nominated Groups - Long term unemployed + Barriers to Employment
    [SocialCommitmentTypeHour.LongTermUnemployed]: {
        colour: "#1565C0",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [
            EmployeeSocialFactor.LongTermUnemployed,
            EmployeeSocialFactor.BarriersToEmployment
        ],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 4
    },
    // Nominated Groups - Graduates, Cadets, Long term unemployed, People with barriers to employment, and Upskilling
    [SocialCommitmentTypeHour.NominatedGroup]: {
        colour: "#3f99fa",
        employmentLevel: [
            EmploymentLevel.Graduate,
            EmploymentLevel.Cadet,
            EmploymentLevel.Upskilling
        ],
        gender: [],
        maxDistance: undefined,
        socialFactors: [
            EmployeeSocialFactor.LongTermUnemployed,
            EmployeeSocialFactor.BarriersToEmployment
        ],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 20
    },
    // SA Workforce Participation (SAWP)
    [SocialCommitmentTypeHour.SAWP]: {
        colour: "#90CAF9",
        employmentLevel: [],
        gender: [],
        maxDistance: 125,
        postCodes: [], // TODO: Filter to SA postcodes only when implementing postcode filtering
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 90
    },
    // Trainees and Apprentices Group (TA Group)
    [SocialCommitmentTypeHour.TAGroup]: {
        colour: "#28A745",
        employmentLevel: [EmploymentLevel.Apprentice, EmploymentLevel.Trainee],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 0 // TODO: Set appropriate target value when provided
    },
    // Women in Construction
    [SocialCommitmentTypeHour.WomenInConstruction]: {
        colour: "#15cf74",
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 3
    }
});
export const southAustraliaLabourHourCommitmentCriteria = createSouthAustraliaLabourHourCommitmentCriteria();
// Queensland, Australia - Labour Hour Commitment Criteria
const createQueenslandLabourHourCommitmentCriteria = () => ({
    // Culturally & Linguistically Diverse [CALD] Workforce
    [SocialCommitmentTypeHour.CulturallyAndLinguisticallyDiverse]: {
        colour: "#3f99fa",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.CulturallyAndLinguisticallyDiverse],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 5
    },
    // Female Participation (enum already exists)
    [SocialCommitmentTypeHour.FemaleParticipation]: {
        colour: "#15cf74", // TODO: Set appropriate colour
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 2
    },
    // Indigenous Participation
    [SocialCommitmentTypeHour.IndigenousParticipation]: {
        colour: "#FFAB91",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 4
    },
    // Local Workforce (region specific)
    [SocialCommitmentTypeHour.LocalWorkforce]: {
        colour: "#90CAF9",
        employmentLevel: [],
        gender: [],
        maxDistance: 125,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 2
    },
    // Project Training Opportunities
    // NOTE: trainingclassification, studentid, evidence, New/ExistingRole fields may need to be added to ProjectLabourHourCommitmentLineItem
    [SocialCommitmentTypeHour.NewTrainingOpportunities]: {
        colour: "#A5D6A7",
        employmentLevel: [
            EmploymentLevel.Apprentice,
            EmploymentLevel.Trainee,
            EmploymentLevel.Cadet
        ],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.ProjectTrainingTargetHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 60
    },
    // Targeted Inclusive Apprentice & Trainee Employment
    // NOTE: trainingclassification, studentid, evidence fields may need to be added to ProjectLabourHourCommitmentLineItem
    [SocialCommitmentTypeHour.ProjectTrainingOpportunities]: {
        colour: "#28A745",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.ProjectTrainingTargetHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 2
    },
    // Other Workforce Training
    // NOTE: This appears to be a variant of ProjectTrainingOpportunities with different criteria
    // TODO: Clarify if this should be a separate enum value or use ProjectTrainingOpportunities with different config
    // [SocialCommitmentTypeHour.OtherWorkforceTraining]: {
    //   colour: "#FFE57F", // TODO: Set appropriate colour
    //   employmentLevel: [],
    //   gender: [],
    //   maxDistance: undefined,
    //   socialFactors: [],
    //   targetDenominator: LabourHourTargetDenominator.TotalWorkHours, // TODO: Use ProjectTrainingTargetHours when available
    //   targetMetricType: ProjectLabourHourCommitmentType.Fixed,
    //   targetType: ProjectLabourHourCommitmentType.Fixed,
    //   targetValue: 40,
    // },
    // New training opportunities
    // NOTE: trainingclassification, studentid, evidence fields may need to be added to ProjectLabourHourCommitmentLineItem
    [SocialCommitmentTypeHour.TargettedInclusiveATC]: {
        colour: "#1565C0",
        employmentLevel: [
            EmploymentLevel.Apprentice,
            EmploymentLevel.Trainee,
            EmploymentLevel.Cadet
        ],
        gender: [],
        maxDistance: undefined,
        newEntrantToConstruction: true,
        socialFactors: [
            EmployeeSocialFactor.LongTermUnemployed,
            EmployeeSocialFactor.PersonWithDisability,
            EmployeeSocialFactor.AboriginalOrTorresStraitIslander,
            EmployeeSocialFactor.CulturallyAndLinguisticallyDiverse,
            EmployeeSocialFactor.Veteran
        ],
        targetDenominator: LabourHourTargetDenominator.ProjectTrainingTargetHours,
        targetMetricType: ProjectLabourHourCommitmentType.Fixed,
        targetType: ProjectLabourHourCommitmentType.Fixed,
        targetValue: 0 // TODO: Set appropriate target value when provided
    }
});
export const queenslandLabourHourCommitmentCriteria = createQueenslandLabourHourCommitmentCriteria();
// ACT (Australian Capital Territory) - Labour Hour Commitment Criteria
const createACTLabourHourCommitmentCriteria = () => ({
    // Canberra Region | Project Team Internal Labour Hours - 70% (100km)
    [SocialCommitmentTypeHour.ACTCanberraInternalLabour]: {
        colour: "#1565C0",
        employmentLevel: [],
        gender: [],
        maxDistance: 100,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 70
    },
    // Canberra Region | FTE - 50% (100km)
    [SocialCommitmentTypeHour.ACTCanberraRegionFTE]: {
        colour: "#90CAF9",
        employmentLevel: [],
        gender: [],
        maxDistance: 100,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 50
    },
    // Canberra Region | Subcontractor Labour Hours - 70% (100km)
    [SocialCommitmentTypeHour.ACTCanberraSubcontractorLabour]: {
        colour: "#7E57C2",
        employmentLevel: [],
        gender: [],
        maxDistance: 100,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 70
    },
    // Female Participation | Non Traditional Roles - 6%
    [SocialCommitmentTypeHour.ACTFemaleNonTraditional]: {
        colour: "#15cf74",
        employmentLevel: [],
        gender: [Gender.Female],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalHoursTrade,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 6
    },
    // Graduate & Cadet Rotations - 0%
    [SocialCommitmentTypeHour.ACTGraduateCadetRotations]: {
        colour: "#A5D6A7",
        employmentLevel: [
            EmploymentLevel.Graduate,
            EmploymentLevel.Cadet,
            EmploymentLevel.Upskilling
        ],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 0
    },
    // Aboriginal & Torres Strait Islander Workforce Participation - 2%
    [SocialCommitmentTypeHour.ACTIndigenousParticipation]: {
        colour: "#FFAB91",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 2
    },
    // New Entrants/Trainees | Learning Workforce - 30%
    [SocialCommitmentTypeHour.ACTLearningWorkforce]: {
        colour: "#3f99fa",
        employmentLevel: [EmploymentLevel.Trainee, EmploymentLevel.Intern],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 30
    },
    // Trade Qualified Apprentices - 25%
    [SocialCommitmentTypeHour.ACTTradeApprentices]: {
        colour: "#28A745",
        employmentLevel: [EmploymentLevel.Apprentice],
        gender: [],
        maxDistance: undefined,
        socialFactors: [],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 25
    },
    // Workforce under 25yo - 15%
    [SocialCommitmentTypeHour.ACTYouthWorkforce]: {
        colour: "#FFE57F",
        employmentLevel: [],
        gender: [],
        maxDistance: undefined,
        socialFactors: [EmployeeSocialFactor.YoungPeopleAgedUnder25],
        targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
        targetMetricType: ProjectLabourHourCommitmentType.Ratio,
        targetType: ProjectLabourHourCommitmentType.Ratio,
        targetValue: 15
    }
});
export const actLabourHourCommitmentCriteria = createACTLabourHourCommitmentCriteria();
// South Australia - Social Spend Commitment Criteria
export const southAustraliaSocialSpendCommitmentCriteria = {
    // Aboriginal Business Spend
    [SocialCommitmentTypeSpend.AboriginalBusinessSpend]: {
        certifications: [
            SocialSpendCertificate.SupplyNationCertified,
            SocialSpendCertificate.Kinaway
        ],
        colour: "#A5D6A7",
        entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
        postCodes: [],
        targetValue: 1.5
    },
    // Local Industry (SA Spend)
    [SocialCommitmentTypeSpend.SASpend]: {
        certifications: [],
        colour: "#90CAF9",
        entityTypes: [SocialSpendEntityType.RegionSpecific],
        postCodes: [], // TODO: Filter to SA postcodes only when implementing postcode filtering
        targetValue: 30
    }
};
// ACT (Australian Capital Territory) - Social Spend Commitment Criteria
export const actSocialSpendCommitmentCriteria = {
    // First Nations Business Addressable Spend - 10%
    [SocialCommitmentTypeSpend.ACTFirstNationsBusinessSpend]: {
        certifications: [
            SocialSpendCertificate.SupplyNationCertified,
            SocialSpendCertificate.SupplyNationRegistered,
            SocialSpendCertificate.Kinaway
        ],
        colour: "#A5D6A7",
        entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
        postCodes: [],
        targetValue: 10
    }
};
// Queensland, Australia - Social Spend Commitment Criteria
export const queenslandSocialSpendCommitmentCriteria = {
    // Aboriginal owned Business Spend (using existing enum with Queensland-specific certifications)
    [SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend]: {
        certifications: [
            SocialSpendCertificate.SupplyNationCertified,
            SocialSpendCertificate.QueenslandIndigenousBusinessNetwork
        ],
        colour: "#A5D6A7",
        entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
        postCodes: [],
        targetValue: 1.5
    },
    // Logan GC Local Procurement
    [SocialCommitmentTypeSpend.LoganGCLocalProcurement]: {
        certifications: [],
        colour: "#90CAF9",
        entityTypes: [SocialSpendEntityType.RegionSpecific],
        postCodes: [
            "4114",
            "4115",
            "4118",
            "4127",
            "4128",
            "4129",
            "4130",
            "4131",
            "4132",
            "4133",
            "4205",
            "4207",
            "4209",
            "4280",
            "4285"
        ],
        targetValue: 30
    },
    // Small - Medium Enterprise Procurement
    [SocialCommitmentTypeSpend.SmallMediumEnterprises]: {
        certifications: [
            SocialSpendCertificate.PeopleAndPlanetFirst,
            SocialSpendCertificate.SocialTraders,
            SocialSpendCertificate.SupplyNationCertified,
            SocialSpendCertificate.BuyQLDVerified
        ],
        colour: "#FFE57F",
        entityTypes: [SocialSpendEntityType.SmallMediumEnterprise],
        postCodes: [],
        targetValue: 1
    }
};
// Sparse location-based defaults map (only locations with defaults are defined)
// To add defaults for a new location, add an entry here
const locationBasedLabourHourDefaults = {
    AU: {
        ACT: actLabourHourCommitmentCriteria,
        QLD: queenslandLabourHourCommitmentCriteria,
        SA: southAustraliaLabourHourCommitmentCriteria,
        VIC: victoriaAustraliaLabourHourCommitmentCriteria
        // Add more Australian states here as needed
        // NSW: newSouthWalesLabourHourCommitmentCriteria,
    }
    // Add more countries here as needed
    // US: {
    //   CA: californiaLabourHourCommitmentCriteria,
    // },
};
const locationBasedSocialSpendDefaults = {
    AU: {
        ACT: actSocialSpendCommitmentCriteria,
        QLD: queenslandSocialSpendCommitmentCriteria,
        SA: southAustraliaSocialSpendCommitmentCriteria,
        VIC: victoriaAustraliaSocialSpendCommitmentCriteria
        // Add more Australian states here as needed
        // NSW: newSouthWalesSocialSpendCommitmentCriteria,
    }
    // Add more countries here as needed
    // US: {
    //   CA: californiaSocialSpendCommitmentCriteria,
    // },
};
// Helper functions to get location-specific defaults
export const getLocationBasedLabourHourDefaults = (country, state) => {
    if (!country || !state) {
        return null;
    }
    return locationBasedLabourHourDefaults[country]?.[state] ?? null;
};
export const getLocationBasedSocialSpendDefaults = (country, state) => {
    if (!country || !state) {
        return null;
    }
    return locationBasedSocialSpendDefaults[country]?.[state] ?? null;
};
/**
 * Get allowed entity types for a given country and state combination
 * Returns all unique entity types that are configured in location-based defaults
 */
export const getAllowedEntityTypesForLocation = (country, state) => {
    const defaults = getLocationBasedSocialSpendDefaults(country, state);
    if (!defaults) {
        // If no location defaults, allow all entity types
        return Object.values(SocialSpendEntityType);
    }
    const allowedEntityTypes = new Set();
    Object.values(defaults).forEach((config) => {
        if (config?.entityTypes && Array.isArray(config.entityTypes)) {
            config.entityTypes.forEach((entityType) => {
                allowedEntityTypes.add(entityType);
            });
        }
    });
    // If no entity types found in defaults, allow all (fallback)
    if (allowedEntityTypes.size === 0) {
        return Object.values(SocialSpendEntityType);
    }
    return Array.from(allowedEntityTypes);
};
/**
 * Get allowed certifications for a given country and state combination
 * Returns all unique certifications that are configured in location-based defaults
 */
export const getAllowedCertificationsForLocation = (country, state) => {
    const defaults = getLocationBasedSocialSpendDefaults(country, state);
    if (!defaults) {
        // If no location defaults, allow all certifications
        return Object.values(SocialSpendCertificate).filter((cert) => cert !== SocialSpendCertificate.None);
    }
    const allowedCertifications = new Set();
    Object.values(defaults).forEach((config) => {
        if (config?.certifications && Array.isArray(config.certifications)) {
            config.certifications.forEach((cert) => {
                if (cert !== SocialSpendCertificate.None) {
                    allowedCertifications.add(cert);
                }
            });
        }
    });
    // If no certifications found in defaults, allow all (fallback)
    if (allowedCertifications.size === 0) {
        return Object.values(SocialSpendCertificate).filter((cert) => cert !== SocialSpendCertificate.None);
    }
    return Array.from(allowedCertifications);
};
/**
 * Get allowed employee social factors for a given country and state combination
 * Returns all unique social factors that are configured in location-based labour hour defaults
 * State-specific factors (e.g., SA's BarriersToEmployment, QLD's CulturallyAndLinguisticallyDiverse)
 * will only be included for their respective states
 */
export const getAllowedSocialFactorsForLocation = (country, state) => {
    const defaults = getLocationBasedLabourHourDefaults(country, state);
    if (!defaults) {
        // If no location defaults, return base social factors (excluding state-specific ones)
        return getBaseSocialFactors();
    }
    const allowedSocialFactors = new Set();
    Object.values(defaults).forEach((config) => {
        if (config?.socialFactors && Array.isArray(config.socialFactors)) {
            config.socialFactors.forEach((factor) => {
                allowedSocialFactors.add(factor);
            });
        }
    });
    // Add base social factors that are common across all states
    getBaseSocialFactors().forEach((factor) => allowedSocialFactors.add(factor));
    return Array.from(allowedSocialFactors);
};
/**
 * State-specific social factors that should only be available for their respective states
 */
const STATE_SPECIFIC_SOCIAL_FACTORS = {
    // Queensland specific factors
    QLD: [EmployeeSocialFactor.CulturallyAndLinguisticallyDiverse],
    // South Australia specific factors
    SA: [EmployeeSocialFactor.BarriersToEmployment]
};
/**
 * Get base social factors that are common across all states
 * This excludes state-specific factors
 */
const getBaseSocialFactors = () => {
    const allStateSpecificFactors = Object.values(STATE_SPECIFIC_SOCIAL_FACTORS).flat();
    return Object.values(EmployeeSocialFactor).filter((factor) => factor !== EmployeeSocialFactor.NewEntrantsToTheConstructionIndustry &&
        !allStateSpecificFactors.includes(factor));
};
